import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { toVideoEmbedUrl } from "../../../shared/video-embed";
import { fmtDate } from "../../lib/format";
import { ThumbnailUploader } from "../../components/ThumbnailUploader";
import { PdfUploader } from "../../components/PdfUploader";
import { RichTextEditor } from "../../components/RichTextEditor";
import {
  SectionHeading,
  StatusBadge,
  ErrorBanner,
  SuccessBanner,
} from "../../components/ui";

interface Row {
  id: string;
  slug: string;
  title: string;
  status: string;
  contentType: string;
  publishedAt: string | null;
  thumbnailKey?: string | null;
}

type ContentType = "article" | "video" | "pdf" | "external_link";
type Status = "draft" | "published" | "private" | "archived";

const TYPE_LABELS: Record<ContentType, string> = {
  article: "記事",
  video: "動画",
  pdf: "PDF",
  external_link: "外部リンク",
};
const STATUS_LABELS: Record<Status, string> = {
  draft: "下書き",
  published: "公開",
  private: "非公開",
  archived: "アーカイブ",
};
const STATUS_TONE: Record<
  Status,
  "success" | "warning" | "danger" | "neutral"
> = {
  draft: "neutral",
  published: "success",
  private: "warning",
  archived: "neutral",
};

interface Form {
  id: string | null; // null = 新規作成、string = 編集対象
  title: string;
  slug: string;
  contentType: ContentType;
  summary: string;
  bodyMarkdown: string;
  status: Status;
  categoryCode: string;
  thumbnailKey: string | null;
  pdfFileKey: string | null;
  videoEmbedUrl: string; // contentType='video' 時のみ
}

const INITIAL: Form = {
  id: null,
  title: "",
  slug: "",
  contentType: "article",
  summary: "",
  bodyMarkdown: "",
  status: "published",
  categoryCode: "",
  thumbnailKey: null,
  pdfFileKey: null,
  videoEmbedUrl: "",
};

export function AdminContentsPage() {
  const [items, setItems] = useState<Row[]>([]);
  const [form, setForm] = useState<Form>(INITIAL);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // 保存時 (server の toEmbedUrl) と同じ変換でプレビューする。
  // ここがずれると「プレビューは映るのに会員側で映らない」事故になる。
  const videoPreviewUrl = useMemo(
    () => toVideoEmbedUrl(form.videoEmbedUrl),
    [form.videoEmbedUrl],
  );

  const load = () =>
    api.get<{ items: Row[] }>("/admin/contents").then((r) => setItems(r.items));
  useEffect(() => {
    load();
  }, []);

  const startEdit = async (id: string) => {
    setErr(null);
    setMsg(null);
    try {
      const d = await api.get<{
        id: string;
        title: string;
        slug: string;
        contentType: string;
        summary: string | null;
        bodyMarkdown: string | null;
        status: string;
        categoryCode?: string | null;
        thumbnailKey?: string | null;
        pdfFileKey?: string | null;
        videoEmbedUrl?: string | null;
      }>(`/admin/contents/${id}`);
      setForm({
        id: d.id,
        title: d.title,
        slug: d.slug,
        contentType: (d.contentType as ContentType) ?? "article",
        summary: d.summary ?? "",
        bodyMarkdown: d.bodyMarkdown ?? "",
        status: (d.status as Status) ?? "draft",
        categoryCode: d.categoryCode ?? "",
        thumbnailKey: d.thumbnailKey ?? null,
        pdfFileKey: d.pdfFileKey ?? null,
        videoEmbedUrl: d.videoEmbedUrl ?? "",
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const cancelEdit = () => {
    setForm(INITIAL);
    setErr(null);
    setMsg(null);
  };

  const submit = async () => {
    setErr(null);
    setMsg(null);
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      setErr("slug は英小文字・数字・ハイフンのみ");
      return;
    }
    const payload = {
      title: form.title,
      slug: form.slug,
      contentType: form.contentType,
      summary: form.summary || null,
      bodyMarkdown: form.bodyMarkdown || null,
      status: form.status,
      categoryCode: form.categoryCode || null,
      thumbnailKey: form.thumbnailKey,
      // pdfFileKey は常に送る (null 含む) ことで、種別切替時に旧 PDF asset を
      // 明示的に削除できる。ただし contentType != 'pdf' なら常に null を送る。
      pdfFileKey: form.contentType === "pdf" ? form.pdfFileKey : null,
      videoEmbedUrl: form.contentType === "video" ? form.videoEmbedUrl || null : null,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/contents/${form.id}`, payload);
        setMsg("更新しました");
      } else {
        await api.post("/admin/contents", payload);
        setMsg("作成しました");
      }
      setForm(INITIAL);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const isEditing = form.id !== null;

  return (
    <div className="space-y-4">
      <SectionHeading level={2}>コンテンツ管理</SectionHeading>
      <div className="card p-4 md:p-5 space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm">
            {isEditing ? "編集" : "新規作成"}
          </h3>
          {isEditing && (
            <button
              onClick={cancelEdit}
              className="text-xs underline text-tora-ink/70"
            >
              キャンセル（新規作成に戻る）
            </button>
          )}
        </div>
        <div>
          <label className="label">タイトル</label>
          <textarea
            className="input min-h-[56px] resize-y"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="改行するとその位置で表示も改行されます"
          />
          <p className="text-[11px] text-tora-ink/50 mt-1">
            Enter で改行できます。改行した位置で会員側の表示も改行されます。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">slug (英小文字・数字・ハイフン)</label>
            <input
              className="input"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
            />
          </div>
          <div>
            <label className="label">種別</label>
            <select
              className="input"
              value={form.contentType}
              onChange={(e) =>
                setForm({ ...form, contentType: e.target.value as ContentType })
              }
            >
              <option value="article">記事</option>
              <option value="video">動画</option>
              <option value="pdf">PDF</option>
              <option value="external_link">外部リンク</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">カテゴリ (任意)</label>
          <select
            className="input"
            value={form.categoryCode}
            onChange={(e) => setForm({ ...form, categoryCode: e.target.value })}
          >
            <option value="">選択しない</option>
            <option value="archive">アーカイブ動画</option>
            <option value="interview">インタビュー記事</option>
            <option value="lecture">講義</option>
            <option value="case">事例</option>
            <option value="document">資料</option>
          </select>
        </div>
        <div>
          <label className="label">概要 (任意)</label>
          <input
            className="input"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
          />
        </div>
        <div>
          <label className="label">本文</label>
          <RichTextEditor
            value={form.bodyMarkdown}
            onChange={(html) => setForm({ ...form, bodyMarkdown: html })}
            placeholder="太字・見出し・リンク・画像など自由にレイアウトできます"
          />
        </div>
        <div>
          <label className="label">ステータス</label>
          <select
            className="input"
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as Status })
            }
          >
            <option value="draft">下書き</option>
            <option value="published">公開</option>
            <option value="private">非公開</option>
            <option value="archived">アーカイブ</option>
          </select>
        </div>
        <ThumbnailUploader
          value={form.thumbnailKey}
          onChange={(k) => setForm({ ...form, thumbnailKey: k })}
        />
        {form.contentType === "video" && (
          <div>
            <label className="label">動画 URL / 埋め込みコード (Vimeo / YouTube)</label>
            <textarea
              className="input min-h-[72px] font-mono text-xs"
              value={form.videoEmbedUrl}
              onChange={(e) =>
                setForm({ ...form, videoEmbedUrl: e.target.value })
              }
              placeholder={
                "例: https://vimeo.com/123456789/0c41057f03\n" +
                "Vimeo の「埋め込みコード」をそのまま貼り付けても構いません"
              }
            />
            <p className="text-[11px] text-tora-ink/50 mt-1">
              Vimeo / YouTube の <b>URL</b>、または Vimeo の <b>埋め込みコード</b> を貼り付けてください。
              自動的に埋込形式に変換されます。
              限定公開 Vimeo を URL で貼る場合は <code>https://vimeo.com/数字/英数字</code> の形のまま
              (末尾の英数字が再生に必要です)。
            </p>
            {videoPreviewUrl ? (
              <div className="mt-2 aspect-video rounded-lg overflow-hidden border border-black/10 bg-black">
                <iframe
                  src={videoPreviewUrl}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                />
              </div>
            ) : (
              form.videoEmbedUrl.trim() !== "" && (
                <p className="mt-2 text-[11px] text-red-600">
                  動画 URL を認識できませんでした。Vimeo / YouTube の URL か埋め込みコードを貼り付けてください。
                </p>
              )
            )}
            {videoPreviewUrl && (
              <p className="text-[11px] text-tora-ink/40 mt-1 break-all">
                保存される埋込 URL: <code>{videoPreviewUrl}</code>
              </p>
            )}
          </div>
        )}
        {form.contentType === "pdf" && (
          <PdfUploader
            value={form.pdfFileKey}
            onChange={(k) => setForm({ ...form, pdfFileKey: k })}
          />
        )}
        {err && <ErrorBanner>{err}</ErrorBanner>}
        {msg && <SuccessBanner>{msg}</SuccessBanner>}
        <button className="btn-primary" onClick={submit}>
          {isEditing ? "更新" : "作成"}
        </button>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full text-sm border border-black/10">
        <thead className="bg-black/5 text-left">
          <tr>
            <th className="p-2">タイトル</th>
            <th className="p-2">slug</th>
            <th className="p-2">種別</th>
            <th className="p-2">ステータス</th>
            <th className="p-2">公開日</th>
            <th className="p-2">操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t border-black/10">
              <td className="p-2">
                <div className="flex items-center gap-2">
                  {c.thumbnailKey ? (
                    <img
                      src={`/api/uploads/${c.thumbnailKey}`}
                      alt=""
                      className="w-10 h-10 object-cover rounded border border-black/10 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded border border-black/10 bg-gray-100 shrink-0" />
                  )}
                  <span>{c.title}</span>
                </div>
              </td>
              <td className="p-2 font-mono text-xs">{c.slug}</td>
              <td className="p-2">
                {TYPE_LABELS[c.contentType as ContentType] ?? c.contentType}
              </td>
              <td className="p-2">
                <StatusBadge
                  tone={STATUS_TONE[c.status as Status] ?? "neutral"}
                >
                  {STATUS_LABELS[c.status as Status] ?? c.status}
                </StatusBadge>
              </td>
              <td className="p-2">{c.publishedAt ? fmtDate(c.publishedAt) : "-"}</td>
              <td className="p-2 flex items-center gap-2">
                <button
                  className="text-tora underline text-xs"
                  onClick={() => startEdit(c.id)}
                >
                  編集
                </button>
                <button
                  className="text-xs text-tora-ink/70 hover:text-tora"
                  onClick={() => {
                    const url = `${window.location.origin}/contents/${c.id}`;
                    navigator.clipboard?.writeText(url);
                  }}
                  title="会員向けリンクをコピー"
                >
                  🔗 リンク
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
